import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import apiService from '../services/api';

const DEFAULT_PAGE_SIZE = 20;
const HIDDEN_COLUMNS = new Set(['ema50', 'ema200', 'adx', 'atr']);
const COLUMN_LABELS = {
  tp2Percent: 'tp2 %',
  tp1Percent: 'tp1 %',
  tp1Price: 'tp1',
  tp2Price: 'tp2',
  exitReason: 'Exit',
  totalUsdt: 'Total USDT',
  pnlUsdt: 'PnL (USDT)',
};

const reorderColumns = (cols) => {
  const out = [...cols];
  const pnlKey = 'pnlUsdt';
  const exitKey = 'exitReason';
  const entryKey = 'entryPrice';

  if (!out.includes(pnlKey) || !out.includes(exitKey) || !out.includes(entryKey)) {
    return out;
  }

  // Move pnlUsdt to be right after exitReason (i.e., between Exit and entryPrice)
  const pnlIdx = out.indexOf(pnlKey);
  out.splice(pnlIdx, 1);
  const exitIdx = out.indexOf(exitKey);
  out.splice(exitIdx + 1, 0, pnlKey);
  return out;
};

const PaginatedTable = ({ rows, formatCellValue, pageSize = DEFAULT_PAGE_SIZE, forcedColumns = null }) => {
  const [page, setPage] = useState(1);

  const safeRows = Array.isArray(rows) ? rows : [];
  const totalRows = safeRows.length;

  const columns = useMemo(() => {
    if (safeRows.length === 0) return [];
    if (forcedColumns) return forcedColumns;
    const allColumns = Object.keys(safeRows[0] || {});
    const filtered = allColumns.filter((c) => !HIDDEN_COLUMNS.has(c));
    return reorderColumns(filtered);
  }, [safeRows, forcedColumns]);

  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  if (columns.length === 0) return null;
  if (totalRows === 0) return null;

  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(totalRows, startIdx + pageSize);
  const pageRows = safeRows.slice(startIdx, endIdx);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-dark-700">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white whitespace-nowrap"
                >
                  {COLUMN_LABELS[col] || col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => (
              <tr key={startIdx + idx} className="border-b border-dark-700 hover:bg-dark-700/30">
                {columns.map((col) => {
                  const value = row[col];
                  const isTrendCol = col.toLowerCase() === 'trend';
                  const isSideCol = col === 'longOrShort' || col === 'buyType';

                  if (isTrendCol || isSideCol) {
                    const formattedValue = formatCellValue(col, value);
                    const normalized = String(formattedValue ?? '').toUpperCase();
                    let badgeClass = 'bg-dark-700 text-dark-200';

                    if (normalized === 'UP' || normalized === 'LONG') {
                      badgeClass = 'bg-green-500 text-white';
                    } else if (normalized === 'DOWN' || normalized === 'SHORT') {
                      badgeClass = 'bg-red-500 text-white';
                    }

                    return (
                      <td key={col} className="px-4 py-3 text-xs sm:text-sm text-dark-200">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClass}`}>
                          {normalized || '-'}
                        </span>
                      </td>
                    );
                  }

                  if (col.toLowerCase().includes('pnl')) {
                    const n = Number(value);
                    const colorClass = Number.isNaN(n)
                      ? 'text-dark-200'
                      : n >= 0
                      ? 'text-green-400'
                      : 'text-red-400';

                    return (
                      <td key={col} className="px-4 py-3 text-xs sm:text-sm">
                        <span className={colorClass}>
                          {formatCellValue(col, value)}
                        </span>
                      </td>
                    );
                  }

                  return (
                    <td key={col} className="px-4 py-3 text-xs sm:text-sm text-dark-200 whitespace-nowrap">
                      {formatCellValue(col, value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="text-xs sm:text-sm text-dark-400">
            Showing {startIdx + 1}-{endIdx} of {totalRows}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-xs sm:text-sm hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <div className="text-xs sm:text-sm text-dark-300">
              Page {page} / {pageCount}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-xs sm:text-sm hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CandlestickChart = ({ candles }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    let chart;
    let candleSeries;
    let cancelled = false;
    let resizeObserver;

    const load = async () => {
      if (!containerRef.current || !Array.isArray(candles) || candles.length === 0) return;
      try {
        const lib = await import('lightweight-charts');
        const createChart = lib?.createChart;
        if (!createChart || cancelled) return;

        chart = createChart(containerRef.current, {
          height: 360,
          layout: {
            background: { type: 'solid', color: '#0f172a' },
            textColor: '#cbd5e1',
          },
          grid: {
            vertLines: { color: 'rgba(148, 163, 184, 0.2)' },
            horzLines: { color: 'rgba(148, 163, 184, 0.2)' },
          },
          rightPriceScale: { borderColor: '#1f2937' },
          timeScale: { borderColor: '#1f2937' },
        });

        if (!chart || typeof chart.addCandlestickSeries !== 'function') return;

        candleSeries = chart.addCandlestickSeries({
          upColor: '#22c55e',
          borderUpColor: '#22c55e',
          wickUpColor: '#22c55e',
          downColor: '#ef4444',
          borderDownColor: '#ef4444',
          wickDownColor: '#ef4444',
        });

        const formatted = candles
          .map((c) => ({
            time: Math.floor((c.closeTimeMs ?? c.tsMs ?? c.time ?? Date.now()) / 1000),
            open: Number(c.open),
            high: Number(c.high),
            low: Number(c.low),
            close: Number(c.close),
          }))
          .filter((c) => ![c.open, c.high, c.low, c.close].some((v) => Number.isNaN(v)));

        candleSeries.setData(formatted);

        const markers = candles
          .map((c) => {
            if (c.signal === 'BUY') {
              return {
                time: Math.floor((c.closeTimeMs ?? c.tsMs ?? c.time ?? Date.now()) / 1000),
                position: 'belowBar',
                color: '#22c55e',
                shape: 'arrowUp',
                text: 'BUY',
              };
            }
            if (c.signal === 'SELL') {
              return {
                time: Math.floor((c.closeTimeMs ?? c.tsMs ?? c.time ?? Date.now()) / 1000),
                position: 'aboveBar',
                color: '#ef4444',
                shape: 'arrowDown',
                text: 'SELL',
              };
            }
            return null;
          })
          .filter(Boolean);

        if (markers.length > 0) {
          candleSeries.setMarkers(markers);
        }

        if (chart.timeScale()?.fitContent) {
          chart.timeScale().fitContent();
        }

        const handleResize = () => {
          const width = containerRef.current?.clientWidth || 0;
          if (width > 0) {
            chart.applyOptions({ width });
          }
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        if (window.ResizeObserver && containerRef.current) {
          resizeObserver = new ResizeObserver(() => handleResize());
          resizeObserver.observe(containerRef.current);
        }

        return () => {
          window.removeEventListener('resize', handleResize);
          if (resizeObserver && containerRef.current) {
            resizeObserver.unobserve(containerRef.current);
          }
        };
      } catch (e) {
        console.error('Failed to render chart', e);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (resizeObserver && containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      if (chart) {
        chart.remove();
      }
    };
  }, [candles]);

  return <div ref={containerRef} className="w-full" />;
};

const Simulator = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('positions');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tradeHistoryView, setTradeHistoryView] = useState('details'); // details | summary
  const [tradeHistorySortColumn, setTradeHistorySortColumn] = useState('');
  const [tradeHistorySortDirection, setTradeHistorySortDirection] = useState('asc');
  const [positionSortColumn, setPositionSortColumn] = useState('');
  const [positionSortDirection, setPositionSortDirection] = useState('asc');
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [strategyDetails, setStrategyDetails] = useState(null);
  const [strategyDetailsError, setStrategyDetailsError] = useState('');
  const [candleData, setCandleData] = useState([]);
  const [candleLoading, setCandleLoading] = useState(false);
  const [candleError, setCandleError] = useState('');
  const [candleInterval, setCandleInterval] = useState('1m');
  const [candleLimit, setCandleLimit] = useState(500);
  const [candleDebug, setCandleDebug] = useState(null);

  const tabs = [
    { id: 'positions', label: 'Positions' },
    { id: 'signals', label: 'Signals' },
    { id: 'tradeHistory', label: 'Trade History' },
    { id: 'currentLoss', label: 'Current Loss' },
    { id: 'drawdown', label: 'Max Drawdown' },
    { id: 'chart', label: 'Chart' },
    { id: 'tradeLogs', label: 'Trade Logs' },
  ];

  const formatCellValue = (col, value) => {
    if (value == null) return '-';

    const lowerCol = col.toLowerCase();
    if (
      lowerCol.includes('price') ||
      lowerCol.includes('pnl') ||
      lowerCol.includes('percent') ||
      lowerCol.includes('usdt') ||
      lowerCol === 'marginused' ||
      col === 'fundingRate' ||
      col === 'score' ||
      col === 'quantity' ||
      col === 'positionAmt'
    ) {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        // Margin used should show only 2 decimals; others keep 5 for precision
        if (lowerCol === 'marginused') {
          return n.toFixed(2);
        }
        return n.toFixed(5);
      }
    }

    if (col === 'runTime' || col === 'entryTime' || col === 'exitTime' || col === 'time' || col === 'openTime') {
      const s = String(value);
      if (s.includes('T')) {
        return s.replace('T', ' ').split('.')[0];
      }
      // If it's a numeric timestamp
      const n = Number(s);
      if (!Number.isNaN(n) && s.length > 10) {
        return new Date(n).toISOString().replace('T', ' ').split('.')[0];
      }
      return s;
    }

    if (col === 'longOrShort' || col === 'buyType') {
      const n = Number(value);
      if (n === 1) return 'LONG';
      if (n === 2) return 'SHORT';
      return String(value);
    }

    return String(value);
  };

  const fetchCandles = async ({ strategyIdNum, symbol }) => {
    if (!symbol) {
      setCandleError('Symbol is required for chart.');
      setCandleData([]);
      setCandleDebug(null);
      return;
    }
    setCandleLoading(true);
    setCandleError('');
    try {
      const effectiveLimit = candleLimit && candleLimit > 0 ? candleLimit : 500;
      const res = await apiService.getStrategyCandles({
        strategyId: strategyIdNum,
        symbol,
        interval: candleInterval || '1m',
        limit: effectiveLimit,
      });
      const payload = res?.candles
        ? res
        : res?.data?.candles
        ? res.data
        : res?.data
        ? res.data
        : res;

      const maybeCandles =
        Array.isArray(payload?.candles)
          ? payload.candles
          : Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

      const candles = Array.isArray(maybeCandles) ? maybeCandles : [];
      if (!Array.isArray(candles) || candles.length === 0) {
        setCandleError('No candle data returned for this selection.');
        setCandleData([]);
        setCandleDebug(null);
        return;
      }
      candles.sort(
        (a, b) =>
          (Number(a.closeTimeMs ?? a.tsMs ?? a.time ?? a.openTimeMs ?? 0) || 0) -
          (Number(b.closeTimeMs ?? b.tsMs ?? b.time ?? b.openTimeMs ?? 0) || 0),
      );
      setCandleData(candles);
      setCandleDebug({
        count: candles.length,
        first: candles[0],
        last: candles[candles.length - 1],
      });
    } catch (e) {
      console.error('Error fetching candles:', e);
      setCandleError('Failed to load candles.');
      setCandleData([]);
      setCandleDebug(null);
    } finally {
      setCandleLoading(false);
    }
  };

  const fetchData = async (tabId, symbol) => {
    const strategyIdNum = Number(selectedStrategyId);
    if (!strategyIdNum || Number.isNaN(strategyIdNum) || strategyIdNum <= 0) {
      setError('Please select a strategy to load data.');
      setData([]);
      return;
    }
    if (tabId === 'chart') {
      setError('');
      setData([]);
      setLoading(false);
      await fetchCandles({ strategyIdNum, symbol });
      return;
    }
    setLoading(true);
    setError('');
    setData([]);
    try {
      let res;
      switch (tabId) {
        case 'positions':
          res = await apiService.getSimulatedPositions({ strategyId: strategyIdNum, symbol });
          break;
        case 'signals':
          res = await apiService.getAllGeneratedSignals({ strategyId: strategyIdNum, symbol });
          break;
        case 'tradeHistory':
          res = await apiService.getSimulatedHistory({ strategyId: strategyIdNum, symbol });
          break;
        case 'currentLoss':
          res = await apiService.getSimulatedCurrentLoss({ strategyId: strategyIdNum, symbol });
          break;
        case 'drawdown':
          // Full drawdown report does not take a symbol filter
          res = await apiService.getSimulatedDrawdown({ strategyId: strategyIdNum });
          break;
        case 'tradeLogs':
          res = await apiService.getSimulatedLogs({ strategyId: strategyIdNum, symbol });
          break;
        default:
          return;
      }
      const finalData = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      const processedData =
        tabId === 'drawdown'
          ? [...finalData].sort(
              (a, b) => Number(b?.maxDrawdown || 0) - Number(a?.maxDrawdown || 0)
            )
          : finalData;
      setData(processedData);
    } catch (e) {
      console.error('Error fetching simulator data:', e);
      setError('Failed to fetch data from the server.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPositions = async () => {
    const strategyIdNum = Number(selectedStrategyId);
    if (!strategyIdNum || Number.isNaN(strategyIdNum) || strategyIdNum <= 0) {
      setError('Please select a strategy to refresh positions.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiService.refreshSimulatedPositions({ strategyId: strategyIdNum, symbol: symbolFilter });
      const finalData = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setData(finalData);
    } catch (e) {
      console.error('Error refreshing simulator positions:', e);
      setError('Failed to refresh positions from the server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const strategyIdNum = Number(selectedStrategyId);
    if (!strategyIdNum || Number.isNaN(strategyIdNum) || strategyIdNum <= 0) {
      setData([]);
      setCandleData([]);
      return;
    }
    fetchData(activeTab, symbolFilter);
  }, [activeTab, selectedStrategyId, symbolFilter, candleInterval, candleLimit]);

  useEffect(() => {
    const loadStrategies = async () => {
      try {
        const res = await apiService.getStrategies();
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setStrategies(data);
      } catch (e) {
        console.error('Failed to load strategies', e);
      }
    };
    loadStrategies();
  }, []);

  useEffect(() => {
    const loadStrategyDetails = async () => {
      const strategyIdNum = Number(selectedStrategyId);
      if (!strategyIdNum || Number.isNaN(strategyIdNum) || strategyIdNum <= 0) {
        setStrategyDetails(null);
        setStrategyDetailsError('');
        return;
      }
      try {
        const res = await apiService.getStrategyById(strategyIdNum);
        setStrategyDetails(res || null);
        setStrategyDetailsError('');
      } catch (e) {
        console.error('Failed to load strategy details', e);
        setStrategyDetails(null);
        setStrategyDetailsError('Failed to load strategy details.');
      }
    };
    loadStrategyDetails();
  }, [selectedStrategyId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(activeTab, symbolFilter);
  };

  const POSITION_COLUMNS = [
    'symbol',
    'pnl',
    'buyType',
    'entryPrice',
    'openTime',
    'sizeUsdt',
    'initialSize',
    'marginUsed',
    'takeProfit',
    'takeProfit2',
    'tp1Size',
    'tp2Size',
    'stopLoss',
    'uniqueId',
  ];

  const computeTotalPnl = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows.reduce((acc, row) => {
      const pnl = Number(row.pnl || row.pnlUsdt || 0);
      return acc + (Number.isNaN(pnl) ? 0 : pnl);
    }, 0);
  };

  const computeTotalMarginUsed = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows.reduce((acc, row) => {
      const margin = Number(row.marginUsed || 0);
      return acc + (Number.isNaN(margin) ? 0 : margin);
    }, 0);
  };


  const renderTotals = (rowsOverride = null) => {
    const rows = rowsOverride || data;
    if (activeTab !== 'positions' && activeTab !== 'tradeHistory' && activeTab !== 'currentLoss') {
      return null;
    }
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const total = computeTotalPnl(rows);
    const totalMargin = computeTotalMarginUsed(rows);
    const count = rows.length;
    return (
      <div className="mb-4 inline-flex flex-wrap items-center gap-4 px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg shadow-md">
        <span className="text-dark-400 text-sm font-medium">Count: <span className="text-white">{count}</span></span>
        {total !== null && (
          <span className="text-dark-400 text-sm font-medium">
            Total PnL:{' '}
            <span className={`text-lg font-bold ${total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${total.toFixed(5)}
            </span>
          </span>
        )}
        {totalMargin !== null && (
          <span className="text-dark-400 text-sm font-medium">
            Total Margin Used:{' '}
            <span className="text-lg font-bold text-white">
              ${totalMargin.toFixed(2)}
            </span>
          </span>
        )}
      </div>
    );
  };

  const sortTradeHistorySummaryRows = (rows) => {
    if (!tradeHistorySortColumn) return rows;
    const dir = tradeHistorySortDirection === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a?.[tradeHistorySortColumn];
      const bv = b?.[tradeHistorySortColumn];
      const numericCols = new Set(['trades', 'totalPnl']);

      if (numericCols.has(tradeHistorySortColumn)) {
        const an = Number(av);
        const bn = Number(bv);
        if (Number.isNaN(an) && Number.isNaN(bn)) return 0;
        if (Number.isNaN(an)) return -1 * dir;
        if (Number.isNaN(bn)) return 1 * dir;
        if (an === bn) return 0;
        return an < bn ? -1 * dir : 1 * dir;
      }

      const as = String(av ?? '');
      const bs = String(bv ?? '');
      const cmp = as.localeCompare(bs);
      return cmp * dir;
    });
  };

  const handleTradeHistorySort = (col) => {
    setTradeHistorySortColumn((prevCol) => {
      if (prevCol === col) {
        setTradeHistorySortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setTradeHistorySortDirection('asc');
      return col;
    });
  };

  const tradeHistorySummaryRows = useMemo(() => {
    if (activeTab !== 'tradeHistory' || tradeHistoryView !== 'summary') return [];
    if (!Array.isArray(data) || data.length === 0) return [];
    const summaryMap = new Map();
    data.forEach((row) => {
      const sym = (row.symbol || '').toUpperCase();
      const pnl = Number(row.pnl || row.pnlUsdt || 0);
      const prev = summaryMap.get(sym) || { symbol: sym, trades: 0, totalPnl: 0 };
      summaryMap.set(sym, {
        symbol: sym,
        trades: prev.trades + 1,
        totalPnl: prev.totalPnl + (Number.isNaN(pnl) ? 0 : pnl),
      });
    });
    const summaryRows = Array.from(summaryMap.values());
    return sortTradeHistorySummaryRows(summaryRows);
  }, [activeTab, tradeHistoryView, data, tradeHistorySortColumn, tradeHistorySortDirection]);

  const tableRows = useMemo(() => {
    if (activeTab === 'tradeHistory' && tradeHistoryView === 'summary') return tradeHistorySummaryRows;
    if (activeTab === 'positions') return sortPositionRows(data);
    return data;
  }, [
    activeTab,
    tradeHistoryView,
    data,
    tradeHistorySummaryRows,
    positionSortColumn,
    positionSortDirection,
  ]);

  const exportRowsToXlsx = (rows, { fileName, sheetName }) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Sheet1');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'export.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const getVisibleColumnsForExport = (rows, forcedColumns = null) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (safeRows.length === 0) return [];
    if (forcedColumns) return forcedColumns;
    const allColumns = Object.keys(safeRows[0] || {});
    const filtered = allColumns.filter((c) => !HIDDEN_COLUMNS.has(c));
    return reorderColumns(filtered);
  };

  const handleExportTradeHistory = () => {
    if (activeTab !== 'tradeHistory') return;
    const rows = Array.isArray(tableRows) ? tableRows : [];
    if (rows.length === 0) return;

    const safe = (s) => String(s ?? '').replace(/[\\/:*?"<>|]+/g, '-').trim();
    const today = new Date();
    const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const strategyPart = safe(strategyDetails?.strategyName || selectedStrategyId || 'strategy');
    const symbolPart = safe(symbolFilter || 'ALL');
    const viewPart = tradeHistoryView === 'summary' ? 'summary' : 'details';
    const fileName = `simulator-trade-history_${strategyPart}_${symbolPart}_${viewPart}_${ymd}.xlsx`;

    const cols = getVisibleColumnsForExport(rows, null);
    const exportRows =
      cols.length === 0
        ? rows
        : rows.map((row) => {
            const out = {};
            cols.forEach((col) => {
              out[COLUMN_LABELS[col] || col] = formatCellValue(col, row?.[col]);
            });
            return out;
          });

    exportRowsToXlsx(exportRows, {
      fileName,
      sheetName: tradeHistoryView === 'summary' ? 'Trade History Summary' : 'Trade History',
    });
  };

  function sortPositionRows(rows) {
    if (!positionSortColumn) return rows;
    const dir = positionSortDirection === 'desc' ? -1 : 1;
    const numericCols = new Set([
      'pnl',
      'pnlUsdt',
      'entryPrice',
      'sizeUsdt',
      'initialSize',
      'marginUsed',
      'takeProfit',
      'takeProfit2',
      'tp1Size',
      'tp2Size',
      'stopLoss',
    ]);
    const dateCols = new Set(['openTime']);

    return [...rows].sort((a, b) => {
      const av = a?.[positionSortColumn];
      const bv = b?.[positionSortColumn];

      if (numericCols.has(positionSortColumn)) {
        const an = Number(av);
        const bn = Number(bv);
        if (Number.isNaN(an) && Number.isNaN(bn)) return 0;
        if (Number.isNaN(an)) return -1 * dir;
        if (Number.isNaN(bn)) return 1 * dir;
        if (an === bn) return 0;
        return an < bn ? -1 * dir : 1 * dir;
      }

      if (dateCols.has(positionSortColumn)) {
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        if (ad === bd) return 0;
        return ad < bd ? -1 * dir : 1 * dir;
      }

      const as = String(av ?? '');
      const bs = String(bv ?? '');
      const cmp = as.localeCompare(bs);
      return cmp * dir;
    });
  }

  const handlePositionSort = (col) => {
    setPositionSortColumn((prevCol) => {
      if (prevCol === col) {
        setPositionSortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setPositionSortDirection('asc');
      return col;
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2 sm:mb-4 sm:mb-1">
          Simulator
        </h3>
        <p className="text-sm sm:text-base text-dark-300">
          Run trading simulations and test strategies without affecting live accounts.
        </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
              <label className="text-xs sm:text-sm text-dark-300 font-medium">
                Strategy
              </label>
              <select
                className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm min-w-[220px]"
                value={selectedStrategyId}
                onChange={(e) => setSelectedStrategyId(e.target.value)}
              >
                <option value="">Select strategy</option>
                {strategies.map((s) => (
                  <option
                    key={s.strategyId || `${s.strategyName}-${s.strategyInput}`}
                    value={s.strategyId || ''}
                  >
                    {s.strategyName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(strategyDetails || strategyDetailsError) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {strategyDetailsError ? (
                <div className="col-span-1 sm:col-span-2 lg:col-span-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm">
                  {strategyDetailsError}
                </div>
              ) : (
                <>
                  <div className="p-3 rounded-lg bg-dark-700 border border-dark-600">
                    <div className="text-xs text-dark-300 uppercase tracking-wide">Strategy</div>
                    <div className="text-sm sm:text-base text-white font-semibold">
                      {strategyDetails?.strategyName || '-'}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-dark-700 border border-dark-600">
                    <div className="text-xs text-dark-300 uppercase tracking-wide">Initial Balance</div>
                    <div className="text-sm sm:text-base text-white font-semibold">
                      {strategyDetails?.initialBalance != null ? Number(strategyDetails.initialBalance).toFixed(2) : '-'}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-dark-700 border border-dark-600">
                    <div className="text-xs text-dark-300 uppercase tracking-wide">Current Balance</div>
                    <div className="text-sm sm:text-base text-white font-semibold">
                      {strategyDetails?.currentBalance != null ? Number(strategyDetails.currentBalance).toFixed(2) : '-'}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-dark-700 border border-dark-600">
                    <div className="text-xs text-dark-300 uppercase tracking-wide">Equity</div>
                    <div className="text-sm sm:text-base text-white font-semibold">
                      {strategyDetails?.equity != null ? Number(strategyDetails.equity).toFixed(2) : '-'}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-dark-700 border border-dark-600">
                    <div className="text-xs text-dark-300 uppercase tracking-wide">Drawdown HWM</div>
                    <div className="text-sm sm:text-base text-white font-semibold">
                      {strategyDetails?.drawdownHighWaterMark != null
                        ? Number(strategyDetails.drawdownHighWaterMark).toFixed(2)
                        : '-'}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-dark-700 border border-dark-600">
                    <div className="text-xs text-dark-300 uppercase tracking-wide">Max Drawdown %</div>
                    <div className="text-sm sm:text-base text-white font-semibold">
                      {strategyDetails?.maxDrawdownPercent != null
                        ? Number(strategyDetails.maxDrawdownPercent).toFixed(2)
                        : '-'}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="flex gap-3 border-b border-dark-700 mb-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 rounded-t transition-colors whitespace-nowrap font-medium text-sm ${
                activeTab === tab.id
                  ? 'bg-dark-700 text-white border-b-2 border-primary-500'
                  : 'text-dark-400 hover:text-white hover:bg-dark-700/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                Symbol contains
              </label>
              <input
                type="text"
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value.toUpperCase())}
                className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 min-w-[200px]"
                placeholder="e.g. BTCUSDT"
              />
            </div>
            {activeTab === 'chart' && (
              <>
                <div className="flex flex-col">
                  <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                    Interval
                  </label>
                  <select
                    value={candleInterval}
                    onChange={(e) => setCandleInterval(e.target.value)}
                    className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 min-w-[120px]"
                  >
                    {['1m', '3m', '5m', '15m', '30m', '1h', '4h', '1d'].map((iv) => (
                      <option key={iv} value={iv}>
                        {iv}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                    Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={candleLimit}
                    onChange={(e) => setCandleLimit(Number(e.target.value) || 0)}
                    className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 min-w-[100px]"
                  />
                </div>
              </>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
            {activeTab === 'positions' && (
              <button
                type="button"
                onClick={handleRefreshPositions}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setSymbolFilter('');
                setCandleData([]);
                fetchData(activeTab, '');
              }}
              className="px-4 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm font-medium hover:bg-dark-600 transition-colors"
            >
              Clear
            </button>
          </form>
        </div>

        <div className="mt-4">
          {error && (
            <div className="p-4 mb-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'chart' ? (
            <div className="space-y-3">
              {!selectedStrategyId && (
                <div className="p-4 bg-dark-900/40 border border-dark-700 rounded-lg text-dark-300 text-sm">
                  Select a strategy to view the chart overview.
                </div>
              )}
              {selectedStrategyId && candleLoading && (
                <div className="p-12 text-center">
                  <div className="inline-block w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mb-4"></div>
                  <p className="text-dark-400 text-sm italic">Loading candles...</p>
                </div>
              )}
              {selectedStrategyId && candleError && (
                <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-300 text-sm">
                  {candleError}
                </div>
              )}
              {selectedStrategyId && !candleLoading && !candleError && candleData.length > 0 && (
                <div className="space-y-3">
                  {candleDebug && (
                    <div className="text-xs text-dark-300 flex flex-wrap gap-4">
                      <span>Count: <span className="text-white font-semibold">{candleDebug.count}</span></span>
                      <span>First: <span className="text-white font-semibold">{candleDebug.first?.closeTimeMs ?? '-'}</span></span>
                      <span>Last: <span className="text-white font-semibold">{candleDebug.last?.closeTimeMs ?? '-'}</span></span>
                    </div>
                  )}
                  <div className="p-3 rounded-lg bg-dark-800 border border-dark-700">
                    <div className="text-xs text-dark-300 uppercase tracking-wide mb-2">Candlestick Chart</div>
                    <CandlestickChart candles={candleData} />
                  </div>
                  {candleDebug && (
                    <div className="p-3 rounded-lg bg-dark-900 border border-dark-700 text-xs text-dark-200 overflow-x-auto">
                      <div className="font-semibold text-white mb-1">Sample candles</div>
                      <pre className="whitespace-pre-wrap break-all">
{JSON.stringify(
  {
    first: candleDebug.first,
    last: candleDebug.last,
  },
  null,
  2,
)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              {selectedStrategyId && !candleLoading && !candleError && candleData.length === 0 && (
                <div className="p-4 bg-dark-900/40 border border-dark-700 rounded-lg text-dark-300 text-sm">
                  No candles available for this selection.
                </div>
              )}
            </div>
          ) : (
            <>
          {activeTab === 'tradeHistory' && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-dark-400 text-xs sm:text-sm font-medium">View:</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTradeHistoryView('details')}
                  className={`px-3 py-1 rounded-md text-xs sm:text-sm border ${
                    tradeHistoryView === 'details'
                      ? 'bg-primary-600 text-white border-primary-500'
                      : 'bg-dark-700 text-dark-200 border-dark-600 hover:bg-dark-600'
                  }`}
                >
                  Details
                </button>
                <button
                  type="button"
                  onClick={() => setTradeHistoryView('summary')}
                  className={`px-3 py-1 rounded-md text-xs sm:text-sm border ${
                    tradeHistoryView === 'summary'
                      ? 'bg-primary-600 text-white border-primary-500'
                      : 'bg-dark-700 text-dark-200 border-dark-600 hover:bg-dark-600'
                  }`}
                >
                  Summary
                </button>
              </div>
              <button
                type="button"
                onClick={handleExportTradeHistory}
                disabled={loading || !Array.isArray(tableRows) || tableRows.length === 0}
                className="ml-auto px-3 py-1 rounded-md text-xs sm:text-sm border bg-dark-700 text-dark-200 border-dark-600 hover:bg-dark-600 disabled:opacity-50"
                title="Download the currently filtered Trade History rows as .xlsx"
              >
                Export to Excel
              </button>
            </div>
          )}

              {activeTab === 'positions' && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-dark-400 text-xs sm:text-sm font-medium">Sort by:</span>
                  {[
                    { key: 'symbol', label: 'Symbol' },
                    { key: 'pnl', label: 'PnL' },
                    { key: 'buyType', label: 'Buy Type' },
                    { key: 'entryPrice', label: 'Entry Price' },
                    { key: 'openTime', label: 'Open Time' },
                    { key: 'sizeUsdt', label: 'Size USDT' },
                    { key: 'initialSize', label: 'Initial Size' },
                    { key: 'marginUsed', label: 'Margin Used' },
                    { key: 'takeProfit', label: 'TP1' },
                    { key: 'takeProfit2', label: 'TP2' },
                    { key: 'tp1Size', label: 'TP1 Size' },
                    { key: 'tp2Size', label: 'TP2 Size' },
                    { key: 'stopLoss', label: 'Stop Loss' },
                    { key: 'uniqueId', label: 'Unique ID' },
                  ].map((btn) => {
                    const isActive = positionSortColumn === btn.key;
                    const dir = positionSortDirection === 'asc' ? '↑' : '↓';
                    return (
                      <button
                        key={btn.key}
                        type="button"
                        onClick={() => handlePositionSort(btn.key)}
                        className={`px-3 py-1 rounded-md text-xs sm:text-sm border transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white border-primary-500'
                            : 'bg-dark-700 text-dark-200 border-dark-600 hover:bg-dark-600'
                        }`}
                      >
                        {btn.label} {isActive ? dir : ''}
                      </button>
                    );
                  })}
                </div>
              )}

              {activeTab === 'tradeHistory' && tradeHistoryView === 'summary' && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-dark-400 text-xs sm:text-sm font-medium">Sort by:</span>
                  {[
                    { key: 'symbol', label: 'Symbol' },
                    { key: 'trades', label: 'Trades' },
                    { key: 'totalPnl', label: 'Total PnL' },
                  ].map((btn) => {
                    const isActive = tradeHistorySortColumn === btn.key;
                    const dir = tradeHistorySortDirection === 'asc' ? '↑' : '↓';
                    return (
                      <button
                        key={btn.key}
                        type="button"
                        onClick={() => handleTradeHistorySort(btn.key)}
                        className={`px-3 py-1 rounded-md text-xs sm:text-sm border transition-colors ${
                          isActive
                            ? 'bg-primary-600 text-white border-primary-500'
                            : 'bg-dark-700 text-dark-200 border-dark-600 hover:bg-dark-600'
                        }`}
                      >
                        {btn.label} {isActive ? dir : ''}
                      </button>
                    );
                  })}
                </div>
              )}

          {renderTotals(
            activeTab === 'tradeHistory' && tradeHistoryView === 'summary'
              ? data // totals based on raw trades (data is raw)
              : data
          )}

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-dark-400 text-sm italic">Loading simulated data...</p>
            </div>
          ) : data.length === 0 ? (
            <div className="p-12 text-center text-dark-500 italic bg-dark-900/30 rounded-xl border border-dashed border-dark-700">
              No data found for the current selection
            </div>
          ) : (
            <PaginatedTable
              rows={tableRows}
              formatCellValue={formatCellValue}
              pageSize={20}
              forcedColumns={activeTab === 'positions' ? POSITION_COLUMNS : null}
            />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Simulator;
